import qs from 'qs'

import {
  wrapCall,
  embedsToQuery,
  arrayToQuery,
  callGet,
  callPost,
  callPut,
  callDelete,
  callPatch,
  arrayToQueryEncode
} from './helpers'
import { DEFAULT_EMBEDS as TASK_EMBEDS } from '../../providers/TaskAPIProvider'
import {
  CustomFieldSearch,
  FIELD_LIMIT,
  getFieldsKeyWords,
  getUrlBySortAndFilters
} from '../../slices/contactsTable'

// Channels will be disabled after this ticket

//* ~~~~~~ USER ROUTES ~~~~~~

// Sends GET to fetch users in the system
export const fetchUsers = async (
  embeds: string[],
  external: boolean,
  limit = 25,
  page = 0,
  ownerId = null,
  sort = []
) => {
  let url = `/users${embedsToQuery(
    embeds
  )}&external=${external}&page=${page}&per_page=${limit}`
  if (ownerId) {
    url += `&owner_id=${ownerId}`
  }
  if (sort.length > 0) {
    url += `&${arrayToQueryEncode('sort', sort)}`
  }
  return (await wrapCall({
    method: callGet,
    uri: url,
    noData: true
  })) as WithIndexPagination<APIUser[]>
}

// Sends GET to fetch users in the system
export const fetchUsersByUrl = async url => {
  return (await wrapCall({
    method: callGet,
    uri: url,
    noData: true
  })) as WithIndexPagination<APIUser[]>
}

// Gets specific users in the system
export const lookupUsers = async (ids: string[], embed: string[] = []) => {
  return (await wrapCall({
    method: callPost,
    uri: '/users/bulk',
    data: { ids, embed }
  })) as APIUser[]
}

// Sends POST to add a user to the system
export const postUser = async (data: UserUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/users',
    data,
    rawError: true
  })) as APIUser
}

// Sends PUT to update a user in the system
export const putUser = async (data: UserUpdate & { id: string }) => {
  const id = data.id
  delete data.id
  return (await wrapCall({
    method: callPut,
    uri: `/users/${id}`,
    data,
    rawError: true
  })) as UserUpdate
}

// Sends GET to find a single user
export const getUser = async (id: string, embeds: string[] = []) => {
  return (await wrapCall({
    method: callGet,
    uri: `/users/${id}${embedsToQuery(embeds)}`
  })) as APIUser
}

// Sends DELETE to remove a user
export const deleteUser = async (id: string) => {
  return await wrapCall({ method: callDelete, uri: `/users/${id}` })
}

export const deleteUsers = async (data: Array<UserPatch>) => {
  return await wrapCall({
    method: callPatch,
    data: { users: data },
    uri: `/users`
  })
}
//* ~~~~~~ STATUSES ~~~~~~

// Gets available user statuses
export const getUserStatuses = async () => {
  return await wrapCall({ method: callGet, uri: '/users/statuses' })
}

// Updates available user statuses
export const putUserStatus = async (id, data) => {
  return await wrapCall({ method: callPut, uri: `/users/statuses/${id}`, data })
}

// Posts available user statuses
export const postUserStatus = async data => {
  return await wrapCall({ method: callPost, uri: '/users/statuses', data })
}

// Delete available user statuses
export const deleteUserStatus = async id => {
  return await wrapCall({ method: callDelete, uri: `/users/statuses/${id}` })
}

//* ~~~~~~ META ~~~~~~

// Sets meta for user
export const putUserMeta = async (id, data) => {
  return await wrapCall({
    method: callPut,
    uri: `/users/${id}/meta`,
    data: { meta: data },
    // meta: data,
    rawError: true
  })
}

// Gets meta for user
export const getUserMeta = async id => {
  return await wrapCall({
    method: callGet,
    uri: `/users/${id}/meta`
  })
}

//* ~~~~~~ FAVORITES ~~~~~~

// Gets favorites for a user
export const getUserFavorites = async (userID: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/users/${userID}/favorites`
  })) as APIFavorite[]
}

// Updates favorites for a user
export const updateUserFavorites = async (
  userID: string,
  data: APIFavorite[]
) => {
  return (await wrapCall({
    method: callPatch,
    uri: `/users/${userID}/favorites`,
    data: { data: data }
  })) as APIFavorite[]
}

//* ~~~~~~ NOTES ~~~~~~

// Gets notes for a user
export const getUserNotes = async (id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/users/${id}/notes`
  })) as UserAPINote[]
}

// Sends note for user
export const postUserNote = async (id: string, data: UserNoteUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: `/users/${id}/notes`,
    data,
    rawError: true
  })) as UserAPINote
}

// Puts note for a user
export const putUserNote = async (
  userId: string,
  noteId: string,
  data: UserNoteUpdate
) => {
  return (await wrapCall({
    method: callPut,
    uri: `/users/${userId}/notes/${noteId}`,
    data
  })) as UserAPINote
}

// Deletes a note for a user
export const deleteUserNote = async (userId: string, noteId: string) => {
  return await wrapCall({
    method: callDelete,
    uri: `/users/${userId}/notes/${noteId}`
  })
}

//* ~~~~~~ TYPES ~~~~~~

// Gets available user types
export const getUserTypes = async () => {
  return await wrapCall({ method: callGet, uri: '/users/types' })
}

// Updates available user types
export const updateUserType = async (id, data) => {
  return await wrapCall({ method: callPut, uri: `/users/types/${id}`, data })
}

// Posts available user types
export const postUserType = async data => {
  return await wrapCall({ method: callPost, uri: '/users/types', data })
}

// Delete available user types
export const deleteUserType = async id => {
  return await wrapCall({ method: callDelete, uri: `/users/types/${id}` })
}

//* ~~~~~~ CHANNEL RELATED ~~~~~~

// Gets conflicting channels for a user id
// used if a check is forced (after adding a new channel for example)
export const getChannelConflicts = async id => {
  return await wrapCall({
    method: callGet,
    uri: `/users/${id}/channels/conflicts`
  })
}

// Merges one user with another
export const mergeContact = async (toId: string, fromId: string) => {
  return (await wrapCall({
    method: callPost,
    uri: `/users/${toId}/merge/${fromId}`
  })) as APIUser
}

// Checks if a channel belongs to another user
export const checkChannelValidation = async (type, value) => {
  return await wrapCall({
    method: callGet,
    uri: `/channels?type=${type}&value=${encodeURIComponent(value)}`
  })
}

// Gets all channels for a user
export const getUserChannels = async (id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/users/${id}/channels`
  })) as APIChannel[]
}

// Create a new channel for a user
export const createUserChannel = async (id: string, data: ChannelUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: `/users/${id}/channels`,
    data
  })) as APIChannel[]
}

// Update existing channel for a user
export const updateUserChannel = async (
  id: string,
  channel: string,
  data: ChannelUpdate
) => {
  return (await wrapCall({
    method: callPut,
    uri: `/users/${id}/channels/${channel}`,
    data
  })) as APIChannel[]
}

// Deletes channel for user by id
export const deleteUserChannel = async (id: string, channel: string) => {
  return await wrapCall({
    method: callDelete,
    uri: `/users/${id}/channels/${channel}`
  })
}

//* ~~~~~~ MISC ~~~~~~

// Updates settings for a user
export const updateUserSettings = async (id, data) => {
  return await wrapCall({ method: callPut, uri: `/users/${id}/settings`, data })
}

// Fetches tasks for dashboard use
export const fetchDashboardTasks = async (id, limit, after) => {
  const query = qs.stringify({ limit, after }, { addQueryPrefix: true })

  const {
    data,
    pagination: { next }
  } = (await wrapCall({
    method: callGet,
    uri: `/users/${id}/tasks${query}`,
    noData: true
  })) as WithTokenPagination<APITask[]>

  return { data, next }
}

// Fetches tasks for a single user
export const fetchUserTasks = async id => {
  const allTasks: APITask[] = []

  const embeds = embedsToQuery(TASK_EMBEDS)
  const uri = `/users/${id}/tasks${embeds}&limit=100`

  // Get all tasks until no next token is found
  let after = undefined
  do {
    const { data, pagination } = (await wrapCall({
      method: callGet,
      uri: after ? `${uri}&after=${after}` : uri,
      noData: true
    })) as WithTokenPagination<APITask[]>
    allTasks.push(...data)
    after = pagination.next
  } while (after)

  return allTasks
}

// Finds users based on keyword
export const searchUser = async (
  keyword,
  channels,
  external = true,
  embeds = [],
  filters = null,
  sort = [],
  fieldInfo: CustomFieldSearch = {
    customFields: [],
    fields: [],
    values: [],
    includedCustom: []
  }
) => {
  // Change
  let embedQuery = ''
  if (embeds.length > 0) {
    embedQuery = `&${arrayToQuery('embed', embeds)}`
  }

  let keywordQuery = ''
  if (keyword.length > 0 || fieldInfo.fields.length > 0) {
    const allKeywords = `${keyword.trim()} ${getFieldsKeyWords(fieldInfo)}`
    keywordQuery = `&keywords=${encodeURIComponent(
      allKeywords.trim().toLocaleLowerCase().padEnd(3).slice(0, 25)
    )}`
  }

  let fieldsQuery = ''
  if (fieldInfo.fields.length > 0) {
    fieldsQuery = `&${arrayToQuery(
      'filter_custom',
      fieldInfo.fields.slice(0, FIELD_LIMIT)
    )}`
  }

  let includedFieldsQuery = ''
  if (fieldInfo.includedCustom.length > 0) {
    includedFieldsQuery = `&${arrayToQuery(
      'include_custom',
      fieldInfo.includedCustom
    )}`
  }

  let channelQuery = ''
  if (channels.length > 0) {
    channelQuery = `&${arrayToQuery('channels', channels)}`
  }

  let uri = `/users?external=${external}${embedQuery}${keywordQuery}${channelQuery}${fieldsQuery}${includedFieldsQuery}`

  if (filters || sort.length > 0) {
    uri += getUrlBySortAndFilters(filters, sort)
  }

  return await wrapCall({
    method: callGet,
    uri: uri
  })
}

// Get aggregate feedback for a survey and user
export const getAggregateFeedback = async (surveyID, userID) => {
  return await wrapCall({
    method: callGet,
    uri: `/feedback/items/${surveyID}/aggregate?user_id=${userID}`
  })
}
